import { useEffect, useMemo, useState } from 'react';
import Invoice from 'shared/db/services/Invoice';
import {
    ACCOUNTING_PROVIDERS,
    AccountingProviderType,
} from 'shared/types/Invoicing';

import { useDBSyncContext } from '@/context/DBSyncContext';
import { useImagesContext } from '@/context/ImagesContext';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { useAccountingProvider } from '@/hooks/useAccountingProvider';
import { useDatabase } from '@/hooks/useDatabase';
import { databaseValidation } from '@/services/databaseValidation';
import { dbServiceLogger } from '@/services/logger/logger';

export function useArchivedInvoicesCount() {
    const { isInitialSyncInProgress } = useDBSyncContext();
    const { ImagesService } = useImagesContext();
    const { getDatabase } = useDatabase();
    const { isInvoiceSystemEnabled } = useInvoicingSystemContext();
    const accountingProvider = useAccountingProvider();

    const connectedProvider: AccountingProviderType | null =
        accountingProvider ?? (isInvoiceSystemEnabled ? 'internal' : null);

    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const invoiceService = useMemo(
        () =>
            new Invoice({
                database: getDatabase(),
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            }),
        [getDatabase, ImagesService],
    );

    useEffect(() => {
        async function fetchArchivedCount() {
            const providers = ACCOUNTING_PROVIDERS.filter(
                (p) => p !== connectedProvider,
            );

            const details = await invoiceService.getArchivedInvoicesDetails(
                providers,
            );

            const sum = Object.values(details).reduce(
                (acc, data) => acc + (data?.count ?? 0),
                0,
            );

            setCount(sum);
            setIsLoading(false);
        }

        if (isInvoiceSystemEnabled && !isInitialSyncInProgress) {
            fetchArchivedCount();
        }
    }, [
        invoiceService,
        isInvoiceSystemEnabled,
        isInitialSyncInProgress,
        connectedProvider,
    ]);

    return { count, isLoading };
}
